import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { useSession } from "@/components/layout/AppShell";
import { ws } from "@/lib/ws";
import type { JobMeta } from "@shared/protocol";
import { relativeTime } from "@/lib/utils";
import {
  COMPILED_LINES_TOOLTIP,
  LINES_OF_CODE_TOOLTIP,
  fmtLines,
} from "@/components/CodeMetrics";

/**
 * FIXED 2026-08-24, direct operator directive: this page was entirely
 * stubbed — three hardcoded "—" stat cards with no data fetch at all, and
 * a hardcoded "No jobs yet" regardless of actual state. Now reuses the
 * same jobs.list WS call JobListPage already makes (already role-scoped
 * server-side — a 'member' sees their own jobs' stats here too, matching
 * what they see on /jobs).
 *
 * "Compiled lines of code" this month — the sum of each job's
 * compiled_lines_of_code (internal/compiler plans/2026-10-12-code-
 * metrics.md), with the loaded total alongside. Jobs built before the
 * metrics existed carry null and are left out of both sums; when no job
 * in view has them yet, the card shows "—" rather than a misleading "0".
 */
function statusVariant(status: JobMeta["status"]) {
  switch (status) {
    case "succeeded":
      return "success" as const;
    case "failed":
      return "destructive" as const;
    case "running":
      return "default" as const;
    default:
      return "secondary" as const;
  }
}

function startOfMonth(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
}

function fmtDuration(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  return `${min}m ${remSec}s`;
}

export function DashboardPage() {
  const { org, user } = useSession();
  const [jobs, setJobs] = useState<JobMeta[] | null>(null);

  useEffect(() => {
    const off = ws.on("jobs.page", (msg) => setJobs(msg.jobs));
    ws.send({ type: "jobs.list" });
    return off;
  }, []);

  const monthStart = startOfMonth();
  const jobsThisMonth = jobs?.filter((j) => j.created_at >= monthStart) ?? [];
  const finishedThisMonth = jobsThisMonth.filter(
    (j) => j.status === "succeeded" && j.started_at && j.finished_at,
  );
  const avgCompileMs = finishedThisMonth.length
    ? finishedThisMonth.reduce(
        (sum, j) => sum + (j.finished_at! - j.started_at!),
        0,
      ) / finishedThisMonth.length
    : null;
  // Jobs that carry code metrics (null on jobs built before they existed).
  const measuredThisMonth = jobsThisMonth.filter(
    (j) => j.compiled_lines_of_code !== null && j.total_lines_of_code !== null,
  );
  const compiledLinesThisMonth = measuredThisMonth.reduce(
    (sum, j) => sum + j.compiled_lines_of_code!,
    0,
  );
  const loadedLinesThisMonth = measuredThisMonth.reduce(
    (sum, j) => sum + j.total_lines_of_code!,
    0,
  );
  const recentJobs = jobs?.slice(0, 5) ?? [];

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            Welcome back{user.name ? `, ${user.name}` : ""}
          </h1>
          <p className="text-muted-foreground text-sm">
            Organization: <span className="font-medium">{org.name}</span>
          </p>
        </div>
        <Link
          to="/jobs/new"
          className="inline-flex items-center gap-2 h-9 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" /> New job
        </Link>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Jobs this month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {jobs ? jobsThisMonth.length : "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Avg. compile time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {avgCompileMs !== null ? fmtDuration(avgCompileMs) : "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle
              className="text-sm text-muted-foreground cursor-help"
              title={COMPILED_LINES_TOOLTIP}
            >
              Compiled lines of code
            </CardTitle>
          </CardHeader>
          <CardContent>
            {jobs !== null && measuredThisMonth.length > 0 ? (
              <>
                <div className="text-2xl font-semibold tabular-nums">
                  {fmtLines(compiledLinesThisMonth)}
                </div>
                <p
                  className="text-xs text-muted-foreground mt-1 cursor-help"
                  title={LINES_OF_CODE_TOOLTIP}
                >
                  of {fmtLines(loadedLinesThisMonth)} lines of code loaded
                </p>
              </>
            ) : (
              <div className="text-2xl font-semibold">—</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent jobs</CardTitle>
        </CardHeader>
        <CardContent>
          {jobs === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : recentJobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No jobs yet.{" "}
              <Link to="/jobs/new" className="underline">
                Submit your first job
              </Link>
              .
            </p>
          ) : (
            <div className="space-y-2">
              {recentJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="flex items-center justify-between rounded-md px-3 py-2 -mx-3 hover:bg-accent/50"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={statusVariant(job.status)}>
                      {job.status}
                    </Badge>
                    <span className="font-mono text-xs">{job.entry}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {relativeTime(job.created_at)}
                  </span>
                </Link>
              ))}
              <div className="pt-2">
                <Link
                  to="/jobs"
                  className="text-sm underline text-muted-foreground"
                >
                  View all jobs →
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
